import { TextField } from '@mui/material'
import dayjs from 'dayjs'
import { Formik } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  DefaultButton,
  DefaultLoadingButton,
} from '../../../common/components/DefaultButton/DefaultButton'
import DatePickerNew from '../../../common/components/InputFieldNew/DatePickerNew'
import ScrollToError from '../../../common/components/ScrollToError/ScrollToError'
import { createTrafficAccidentFormValidationSchema } from '../utils/form'

import AccidentAdditionalDetailsForm from './AccidentAdditionalDetailsForm'
import { ButtonContainer } from './FormComponents'

export type TrafficAccidentInformation = {
  incidentDate?: Date
  incidentDescription: string
  vehicleRegistrationNumber: string
}

interface Props {
  onSubmit(data?: TrafficAccidentInformation): void
  onBack(): void
}

const TrafficAccidentForm: React.FC<React.PropsWithChildren<Props>> = ({ onSubmit, onBack }) => {
  const { t } = useTranslation()

  const validationSchema = createTrafficAccidentFormValidationSchema(t)

  const initialValues: TrafficAccidentInformation = {
    vehicleRegistrationNumber: '',
    incidentDescription: '',
  }

  return (
    <Formik<typeof initialValues>
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnBlur={true}
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
        <AccidentAdditionalDetailsForm onContinue={() => onSubmit()} onBack={onBack}>
          <ScrollToError />
          <TextField
            id="vehicleRegistrationNumber"
            label={t('component.trafficAccidentForm.vehicleRegistrationNumber')}
            placeholder={t('component.trafficAccidentForm.vehicleRegistrationNumberPlaceholder')}
            helperText={touched.vehicleRegistrationNumber && errors.vehicleRegistrationNumber}
            error={Boolean(touched.vehicleRegistrationNumber && errors.vehicleRegistrationNumber)}
            value={values.vehicleRegistrationNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            data-cy="trafficAccidentVehicleRegistrationNumber"
          />
          <DatePickerNew
            id="incidentDate"
            label={t('component.trafficAccidentForm.incidentDate')}
            helperText={touched.incidentDate && errors.incidentDate}
            error={Boolean(touched.incidentDate && errors.incidentDate)}
            value={values.incidentDate}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            data-cy="trafficAccidentIncidentDate"
            max={dayjs().format('YYYY-MM-DD')}
          />
          <TextField
            id="incidentDescription"
            label={t('component.trafficAccidentForm.incidentDescription')}
            placeholder={t('component.trafficAccidentForm.incidentDescriptionPlaceholder')}
            helperText={touched.incidentDescription && errors.incidentDescription}
            error={Boolean(touched.incidentDescription && errors.incidentDescription)}
            value={values.incidentDescription}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            data-cy="trafficAccidentIncidentDescriptionField"
          />
          <ButtonContainer>
            <DefaultLoadingButton type="submit" loading={isSubmitting} disabled={isSubmitting}>
              {t('common.continue')}
            </DefaultLoadingButton>
            <DefaultButton type="button" variant="outlined" onClick={onBack}>
              {t('common.back')}
            </DefaultButton>
          </ButtonContainer>
        </AccidentAdditionalDetailsForm>
      )}
    </Formik>
  )
}

export default TrafficAccidentForm
